import { useCallback, useMemo, useState } from 'react';
import { ChakraFlex, ModalV2, Text } from '@equip.health/ui';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { type RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';
import { type ManifestationCodeToEditableDataMap } from '~/lib/types/carePlan/ManifestationCodeToEditableDataMap';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { useGetCarePlansByTreatmentEpisodeIdQuery } from '~/store/services/carePlan';
import { AddMultiProblemModal } from './AddMultiProblemModal';
import { AddProblemDetailsModal } from './AddProblemDetailsModal';
import { AddCustomInterventionModal } from './AddCustomInterventionModal';

type Step = 'SELECT' | 'DETAILS' | 'INTERVENTIONS';

export type SavedProblem = {
  id: string;
  problem: RefCarePlanProblemResponse;
};

export type EdProblemDetails = {
  description: string;
  frequencyCode?: string;
  manifestationsMap: ManifestationCodeToEditableDataMap;
};

export type CoOccurringProblemDetails = {
  description: string;
};

export type ProblemDetails = EdProblemDetails | CoOccurringProblemDetails;

export type ProblemDetailsByCode = Record<string, ProblemDetails>;

const emptyDetailsFor = (problemTypeCode: ProblemTypeCode): ProblemDetails =>
  problemTypeCode === ProblemTypeCode.EATING_DISORDER
    ? { description: '', manifestationsMap: {} }
    : { description: '' };

export type CarePlanProblemDialogManagerProps = {
  onClose: () => void;
  patientId: string;
  problemTypeCode: ProblemTypeCode;
  treatmentEpisodeId: string;
  noteExternalId?: string;
};

export const CarePlanProblemDialogManager = ({
  onClose,
  patientId,
  problemTypeCode,
  treatmentEpisodeId,
  noteExternalId,
}: CarePlanProblemDialogManagerProps) => {
  const [workflowStep, setWorkflowStep] = useState<Step>('SELECT');
  const [selectedProblemsByCode, setSelectedProblemsByCode] = useState<
    Record<string, RefCarePlanProblemResponse>
  >({});
  const [problemDetailsByCode, setProblemDetailsByCode] =
    useState<ProblemDetailsByCode>({});
  const [savedProblems, setSavedProblems] = useState<SavedProblem[]>([]);
  const [savedCarePlanId, setSavedCarePlanId] = useState<string | null>(null);

  const { data: carePlans } =
    useGetCarePlansByTreatmentEpisodeIdQuery(treatmentEpisodeId);
  const activeCarePlan = useMemo(
    () => carePlans?.find((cp) => cp.status.code === CarePlanStatus.ACTIVE),
    [carePlans],
  );
  const savedCarePlan = useMemo(
    () => carePlans?.find((cp) => cp.id === savedCarePlanId),
    [carePlans, savedCarePlanId],
  );

  const toggleProblem = useCallback(
    (problem: RefCarePlanProblemResponse) => {
      setSelectedProblemsByCode((prev) => {
        if (prev[problem.code]) {
          const { [problem.code]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [problem.code]: problem };
      });
      setProblemDetailsByCode((prev) => {
        if (prev[problem.code]) return prev;
        return { ...prev, [problem.code]: emptyDetailsFor(problemTypeCode) };
      });
    },
    [problemTypeCode],
  );

  const updateProblemDetails = useCallback(
    (problemCode: string, next: ProblemDetails) => {
      setProblemDetailsByCode((prev) => ({ ...prev, [problemCode]: next }));
    },
    [],
  );

  const selectedProblems = Object.values(selectedProblemsByCode);

  if (workflowStep === 'SELECT') {
    return (
      <AddMultiProblemModal
        onClose={onClose}
        onSubmit={() => setWorkflowStep('DETAILS')}
        onToggleProblem={toggleProblem}
        patientId={patientId}
        problemTypeCode={problemTypeCode}
        selectedProblemsByCode={selectedProblemsByCode}
        treatmentEpisodeId={treatmentEpisodeId}
      />
    );
  }

  if (workflowStep === 'DETAILS') {
    return (
      <AddProblemDetailsModal
        activeCarePlan={activeCarePlan}
        noteExternalId={noteExternalId}
        onBack={() => setWorkflowStep('SELECT')}
        onClose={onClose}
        onSubmitSuccess={(saved, carePlanId) => {
          if (problemTypeCode === ProblemTypeCode.EATING_DISORDER) {
            onClose();
            return;
          }
          setSavedProblems(saved);
          setSavedCarePlanId(carePlanId);
          setWorkflowStep('INTERVENTIONS');
        }}
        problemDetailsByCode={problemDetailsByCode}
        problems={selectedProblems}
        problemTypeCode={problemTypeCode}
        treatmentEpisodeId={treatmentEpisodeId}
        updateProblemDetails={updateProblemDetails}
      />
    );
  }

  // workflowStep === 'INTERVENTIONS'
  // Keep the modal surface while the refetched care plan settles from cache. If the refetch fails, the user still has an onClose
  // affordance rather than inline text on the care plan page with no way to dismiss.
  if (!savedCarePlan) {
    return (
      <ModalV2
        id="care-plan-problem-dialog-manager-loading"
        isOpen
        onClose={onClose}
        size="3xl"
        title="Add interventions"
      >
        <ChakraFlex direction="column" padding="18px">
          <Text variant="bodyMedium">Loading care plan…</Text>
        </ChakraFlex>
      </ModalV2>
    );
  }

  return (
    <AddCustomInterventionModal
      cancelLabel="Close without creating interventions"
      carePlan={savedCarePlan}
      leftContent={
        <SavedProblemsPanel
          problemTypeCode={problemTypeCode}
          savedProblems={savedProblems}
        />
      }
      noteExternalId={noteExternalId}
      onClose={onClose}
      saveLabel="Add to care plan"
      subHeading="Indicate how the selected problem(s) will be addressed."
      title="Add interventions"
      treatmentEpisodeId={treatmentEpisodeId}
    />
  );
};

type SavedProblemsPanelProps = {
  problemTypeCode: ProblemTypeCode;
  savedProblems: SavedProblem[];
};

const SavedProblemsPanel = ({
  problemTypeCode,
  savedProblems,
}: SavedProblemsPanelProps) => {
  // Psychiatric and ED problems: flat list
  if (problemTypeCode !== ProblemTypeCode.MEDICAL) {
    return (
      <ChakraFlex direction="column" gap="8px">
        <Text variant="h5">Just added</Text>
        {savedProblems.map(({ id, problem }) => (
          <Text key={id} variant="bodyMedium">
            {problem.label}
          </Text>
        ))}
      </ChakraFlex>
    );
  }

  const bodySystemGroups = new Map<string, SavedProblem[]>();
  savedProblems.forEach((saved) => {
    const bodySystem = saved.problem.bodySystem || 'Other';
    const list = bodySystemGroups.get(bodySystem) ?? [];
    list.push(saved);
    bodySystemGroups.set(bodySystem, list);
  });
  const sortedBodySystems = Array.from(bodySystemGroups.keys()).sort((a, b) =>
    a.localeCompare(b),
  );

  return (
    <ChakraFlex direction="column" gap="12px">
      <Text variant="h5">Just added</Text>
      {sortedBodySystems.map((bodySystem) => (
        <ChakraFlex direction="column" gap="4px" key={bodySystem}>
          <Text color="neutral.secondary" variant="bodyMedium">
            {bodySystem}
          </Text>
          {bodySystemGroups.get(bodySystem)!.map(({ id, problem }) => (
            <Text key={id} variant="bodyMedium">
              {problem.label}
            </Text>
          ))}
        </ChakraFlex>
      ))}
    </ChakraFlex>
  );
};
